import { useEffect } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';

export default function VenturesPage() {
  useEffect(() => {
    document.title = 'Ventures | AfriStakes & Henatos Ventures';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'AfriStakes empowers scalable African businesses through access to structured capital, while Henatos Ventures drives efficiency and transparency in regional trade and exports.');
    }
    
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', `${import.meta.env.VITE_SITE_URL || 'https://henryadebisi.com'}/ventures`);
    }
    
    const keywords = document.querySelector('meta[name="keywords"]');
    if (keywords) {
      keywords.setAttribute('content', 'AfriStakes, Henatos Ventures, structured capital, regional trade, African businesses');
    }

    // Add Schema.org JSON-LD
    const existingSchema = document.querySelector('script[type="application/ld+json"]');
    if (existingSchema) {
      existingSchema.remove();
    }

    const schema = {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Ventures | AfriStakes & Henatos Ventures",
      "description": "AfriStakes empowers scalable African businesses through access to structured capital, while Henatos Ventures drives efficiency and transparency in regional trade and exports.",
      "url": `${import.meta.env.VITE_SITE_URL || 'https://henryadebisi.com'}/ventures`,
      "mainEntity": [
        {
          "@type": "Organization",
          "name": "AfriStakes",
          "description": "A digital funding framework empowering African SMEs with access to sustainable investment.",
          "url": "https://afristakes.com",
          "founder": {
            "@type": "Person",
            "name": "Henry Olamide Adebisi"
          },
          "industry": "Financial Technology"
        },
        {
          "@type": "Organization",
          "name": "Henatos Ventures",
          "description": "Cross-border trade and export operations delivering quality, transparency, and efficiency.",
          "url": "https://henatosbiz.com",
          "founder": {
            "@type": "Person",
            "name": "Henry Olamide Adebisi"
          },
          "industry": "International Trade"
        }
      ]
    };

    const schemaScript = document.createElement('script');
    schemaScript.type = 'application/ld+json';
    schemaScript.textContent = JSON.stringify(schema);
    document.head.appendChild(schemaScript);

    return () => {
      const schemaToRemove = document.querySelector('script[type="application/ld+json"]');
      if (schemaToRemove) {
        schemaToRemove.remove();
      }
    };
  }, []);

  const ventures = [
    {
      name: 'AfriStakes',
      description: 'A digital funding framework empowering African SMEs with access to sustainable investment. Building bridges between real economy businesses and structured capital markets.',
      website: 'https://afristakes.com',
      features: [
        'Structured capital access for African SMEs',
        'Digital investment frameworks',
        'Real economy focus',
        'Cross-border funding solutions'
      ],
      image: 'https://public.readdy.ai/ai/img_res/71c2358aa71ca2e64ab5cfe6d5544c7a.jpg'
    },
    {
      name: 'Henatos Ventures',
      description: 'Cross-border trade and export operations delivering quality, transparency, and efficiency. Optimizing supply chains and facilitating seamless international commerce across Africa.',
      website: 'https://henatosbiz.com',
      features: [
        'Cross-border trade facilitation',
        'Supply chain optimization',
        'Export operations management',
        'Quality assurance systems'
      ],
      image: 'https://readdy.ai/api/search-image?query=Modern%20logistics%20and%20trade%20operations%20center%20with%20African%20professionals%20managing%20cross-border%20commerce%2C%20shipping%20containers%2C%20international%20trade%2C%20professional%20business%20environment%2C%20supply%20chain%20management&width=600&height=400&seq=henatos&orientation=landscape'
    }
  ];

  return (
    <div className="min-h-screen bg-off-white">
      <Header />
      
      <main>
        {/* Hero Section */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="font-sora font-bold text-4xl lg:text-5xl text-navy mb-6">
              Building Africa's Financial Future
            </h1>
            <p className="text-gray-600 text-lg max-w-3xl mx-auto leading-relaxed">
              Through strategic ventures, Henry is creating innovative solutions that bridge the gap between 
              African businesses and global opportunities, focusing on sustainable growth and financial inclusion.
            </p>
          </div>
        </section>

        {/* Ventures Section */}
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="space-y-20">
              {ventures.map((venture, index) => (
                <div key={index} className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${index % 2 === 1 ? 'lg:grid-flow-col-dense' : ''}`}>
                  <div className={index % 2 === 1 ? 'lg:col-start-2' : ''}>
                    <div className="bg-white rounded-lg p-8 shadow-lg">
                      <h2 className="font-sora font-bold text-3xl text-navy mb-4">
                        {venture.name}
                      </h2>
                      <p className="text-gray-700 text-lg mb-6 leading-relaxed">
                        {venture.description}
                      </p>
                      
                      <div className="mb-8">
                        <h3 className="font-sora font-semibold text-lg text-navy mb-4">Key Features:</h3>
                        <ul className="space-y-2">
                          {venture.features.map((feature, featureIndex) => (
                            <li key={featureIndex} className="flex items-center text-gray-600">
                              <i className="ri-check-line text-gold mr-3"></i>
                              {feature}
                            </li>
                          ))}
                        </ul>
                      </div>
                      
                      <Button variant="outline" href={venture.website} className="mr-4">
                        <i className="ri-external-link-line mr-2"></i>
                        Visit Website
                      </Button>
                    </div>
                  </div>
                  
                  <div className={index % 2 === 1 ? 'lg:col-start-1' : ''}>
                    <img 
                      src={venture.image}
                      alt={`${venture.name} operations`}
                      className="rounded-lg shadow-xl object-cover object-top w-full h-96"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Partnership CTA */}
        <section className="py-20 bg-navy">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="font-sora font-bold text-3xl lg:text-4xl text-white mb-6">
              Partner with Henry
            </h2>
            <p className="text-gray-300 text-lg mb-8 leading-relaxed">
              Explore collaboration opportunities in fintech innovation, strategic finance, 
              and sustainable business growth across African markets.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button variant="secondary" size="lg" href="/contact">
                Discuss Partnership
              </Button>
              <Button variant="outline" size="lg" href="/speaking" className="border-white text-white hover:bg-white hover:text-navy bg-white/20 backdrop-blur-sm font-semibold">
                Book Speaking Engagement
              </Button>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
